/*
 *  -----------------------------------------------------------------------
 *    <copyright file="custom-autocomplete/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import FormControl from '@mui/material/FormControl';
import { AutoComplete, AutoCompleteChangeEvent } from '@progress/kendo-react-dropdowns';
import cn from 'clsx';
import React, { useState } from 'react';
import { useBoolean, useTranslation } from '@hooks/index';

import './syles.scss';

interface CustomAutocompleteProps {
    label: string;
    placeholder: string;
    disabled: boolean;
    options: string[];
    value: string;
    customLabelId?: string | undefined;
    onChange: (value: string) => void;
    error?: string;
    className?: string;
}

const CustomAutocomplete = ({
    error,
    className,
    label,
    placeholder,
    disabled,
    options,
    value = '',
    customLabelId,
    onChange,
}: CustomAutocompleteProps) => {
    const { t } = useTranslation();
    const isFocus = useBoolean(false);
    const classNamesLabel = cn({ 'on-focus': isFocus.value, disabled });
    const [state, setState] = useState({ data: options, value });

    const findOptions = (v: string) =>
        options.filter((option: string) => option.toLowerCase().includes(v.toLowerCase()));

    const filterData = (v: string) => {
        const filteredOptions = findOptions(v);

        return v ? filteredOptions : options;
    };

    const onValueChange = (event: AutoCompleteChangeEvent) => {
        const { value: currentValue } = event.target;

        setState({
            data: filterData(currentValue),
            value: currentValue,
        });

        onChange(currentValue);
    };

    return (
        <FormControl
            variant="outlined"
            className={cn('autocomplete-wrapper', className, classNamesLabel, { 'has-error': error })}
        >
            <span id={customLabelId} className={cn('select-with-categories-label', classNamesLabel, { error })}>
                {label}
            </span>
            <AutoComplete
                data={state.data}
                value={state.value}
                onChange={onValueChange}
                disabled={disabled}
                onClose={() => {
                    const hasCorrectValue = !!findOptions(state.value).length;

                    if (!hasCorrectValue) {
                        setState({
                            ...state,
                            value: '',
                        });

                        onChange('');
                    }
                }}
                onFocus={() => {
                    isFocus.setTrue();
                }}
                onBlur={() => {
                    isFocus.setFalse();
                }}
                placeholder={placeholder}
                className="autocomplete"
            />
            {error && <p className="error-message">{error}</p>}
        </FormControl>
    );
};

export default CustomAutocomplete;
